import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { addDays, startOfDay } from 'date-fns';
import {
    useAvailability,
    AvailabilityFilterValues,
    AvailabilityProduct,
    PaxData,
    ProductType,
} from '@travelity/api';
import { useLocation, useNavigate } from 'react-router-dom';
import { isEmpty, pickBy } from 'lodash';
import { PackageBookingSideform } from './components/PackageBookingSideform';
import { Filters } from '../../components/filters';
import {
    FilterOption,
    FilterTypes,
} from '../../components/filters/filters.types';
import { useDynamicState } from '../../hooks';
import { SideForm } from '../../components/side-form';
import BookingSideForm from './components/booking-side-form';
import { productTypeOptions } from '../select-type/select-type';
import { AvailabilityCalendar } from './components/availability-calendar';
import { useSuperUser } from '../../contexts/user';

const availabilityFilters: FilterOption[] = [
    {
        name: 'dates',
        label: 'Dates',
        required: true,
        preventPast: true,
        type: FilterTypes.DATES,
        selectText: 'Please, select the value for date filters',
    },
    {
        name: 'pax',
        label: 'PAX',
        required: true,
        type: FilterTypes.PAX,
        // selectText: 'Please, select the value for product type',
    },
    {
        name: 'productTypes',
        label: 'Product Types',
        type: FilterTypes.DROPDOWN,
        multiple: true,
        selectText: 'Please, select the value for product types',
        options: productTypeOptions,
    },
    {
        name: 'product',
        label: 'Product',
        multiple: true,
        type: FilterTypes.SEARCH_PRODUCT,
        selectText: 'Please, select product',
        options: [],
    },
];

function Availability() {
    const location = useLocation();
    const [filters, setFilters] = useDynamicState<AvailabilityFilterValues>(
        useMemo(() => {
            if (location.state) {
                const stateFilters = pickBy(
                    location.state as AvailabilityFilterValues,
                    v => v
                );
                if (!isEmpty(stateFilters)) return stateFilters;
            }
            return {
                pax: {
                    adults: 1,
                },
                dates: {
                    startDate: startOfDay(new Date()),
                    endDate: addDays(startOfDay(new Date()), 7),
                },
            };
        }, [location.state])
    );

    const navigate = useNavigate();
    const updateFilters = useCallback(
        (newFilters: AvailabilityFilterValues) => {
            navigate('/availability', {
                replace: true,
                state: newFilters,
            });
        },
        [setFilters]
    );

    const {
        data: availability,
        isLoading,
        refetch,
    } = useAvailability(
        useMemo(
            () => ({
                requestBody: filters,
            }),
            [filters]
        )
    );

    const [selectedEvent, setSelectedEvent] = useState<
        | {
              product: AvailabilityProduct;
              time: number;
              day: number;
          }
        | undefined
    >();

    const closeEvent = useCallback(() => {
        setSelectedEvent(undefined);
    }, []);

    useEffect(closeEvent, [filters]);

    const onBookingCreate = useCallback(() => {
        closeEvent();
        refetch();
    }, []);

    const superUser = useSuperUser();
    const availabilityFiltersProcessed = useMemo(() => {
        return superUser
            ? availabilityFilters.map(f =>
                  f.name === 'dates'
                      ? {
                            ...f,
                            preventPast: false,
                        }
                      : f
              )
            : availabilityFilters;
    }, [availabilityFilters, superUser]);

    const openEvent = useCallback(
        (product: AvailabilityProduct, day: number, time: number) => {
            setSelectedEvent({
                product,
                day,
                time,
            });
        },
        [filters]
    );

    const changeTime = useCallback((time: number) => {
        setSelectedEvent(prevState =>
            prevState
                ? {
                      ...prevState,
                      time,
                  }
                : undefined
        );
    }, []);

    return (
        <Stack
            direction="row"
            sx={{ height: 'calc(100vh - 60px)', overflow: 'hidden' }}
        >
            <Box
                sx={{
                    height: '100%',
                    overflow: 'hidden',
                    flexGrow: 2,
                    pr: 2,
                    pl: 4,
                }}
            >
                <Filters
                    values={filters}
                    setValues={updateFilters}
                    options={availabilityFiltersProcessed}
                />
                <AvailabilityCalendar
                    events={availability?.events}
                    isLoading={isLoading}
                    openEvent={openEvent}
                    selectedDay={selectedEvent?.day}
                    selectedTime={selectedEvent?.time}
                    selectedProductId={selectedEvent?.product.id}
                />
            </Box>

            <SideForm
                title="Booking Creation"
                isOpen={!!selectedEvent}
                onClose={closeEvent}
            >
                {!!selectedEvent &&
                    selectedEvent.product.type ===
                        ProductType.DYNAMIC_PACKAGE && (
                        <PackageBookingSideform
                            selectedEvent={selectedEvent.product}
                            selectedTime={selectedEvent.time}
                            pax={filters.pax as PaxData}
                            discard={closeEvent}
                            onSuccess={onBookingCreate}
                        />
                    )}
                {!!selectedEvent &&
                    selectedEvent.product.type !==
                        ProductType.DYNAMIC_PACKAGE && (
                        <BookingSideForm
                            pax={filters.pax as PaxData}
                            products={availability?.products}
                            availableProduct={selectedEvent?.product}
                            time={selectedEvent?.time}
                            changeTime={changeTime}
                            discard={closeEvent}
                            onSuccess={onBookingCreate}
                        />
                    )}
            </SideForm>
        </Stack>
    );
}

export default Availability;
